import React, { useEffect, useRef, useState } from 'react';
import {
  getMezzi, getManutenzioni, createManutenzione,
  updateManutenzione, deleteManutenzione,
  exportManutenzioniXlsx, importManutenzioniXlsx,
  Mezzo, Manutenzione,
} from '../../services/fleet';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const TIPOLOGIE = [
  'motore', 'trasmissione', 'sospensioni', 'carrozzeria',
  'impianto_elettrico', 'freni', 'pneumatici', 'climatizzazione', 'altro',
];

const TIPO_ICONS: Record<string, string> = {
  motore: '🔥', trasmissione: '⚙️', sospensioni: '🔩', carrozzeria: '🚗',
  impianto_elettrico: '⚡', freni: '🛑', pneumatici: '🛞',
  climatizzazione: '❄️', altro: '🔧',
};

const EMPTY: Partial<Manutenzione> = {
  tipologia: 'motore',
  data: new Date().toISOString().split('T')[0],
};

export default function ManutenzioneFleet() {
  const [mezzi, setMezzi] = useState<Mezzo[]>([]);
  const [interventi, setInterventi] = useState<Manutenzione[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterMezzo, setFilterMezzo] = useState<number | ''>('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterAnno, setFilterAnno] = useState<number>(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Manutenzione | null>(null);
  const [form, setForm] = useState<Partial<Manutenzione>>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const anni = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const handleExport = async () => {
    try {
      const params: Record<string, unknown> = { anno: filterAnno };
      if (filterMezzo) params.mezzo_id = filterMezzo;
      if (filterTipo) params.tipologia = filterTipo;
      const res = await exportManutenzioniXlsx(params);
      downloadBlob(res.data as Blob, 'fleet_manutenzione.xlsx');
    } catch { setError('Errore export'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importManutenzioniXlsx(file);
      const { created, updated, errors } = res.data;
      alert(`Import completato: ${created} creati, ${updated} aggiornati${errors.length ? '\nErrori: ' + errors.join('\n') : ''}`);
      load();
    } catch { setError('Errore import'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  useEffect(() => {
    getMezzi(true).then(r => setMezzi(r.data));
    load();
  }, []);

  useEffect(() => { load(); }, [filterMezzo, filterTipo, filterAnno]);

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { anno: filterAnno };
      if (filterMezzo) params.mezzo_id  = filterMezzo;
      if (filterTipo)  params.tipologia = filterTipo;
      const res = await getManutenzioni(params);
      setInterventi(res.data);
    } catch {
      setError('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, mezzo_id: filterMezzo || undefined });
    setError('');
    setShowForm(true);
  };

  const openEdit = (s: Manutenzione) => {
    setEditing(s);
    setForm({ ...s });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.mezzo_id) { setError('Seleziona un veicolo'); return; }
    if (!form.data)     { setError('Data obbligatoria'); return; }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateManutenzione(editing.id, form);
      } else {
        await createManutenzione(form);
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminare questo intervento?')) return;
    try {
      await deleteManutenzione(id);
      load();
    } catch {
      setError('Errore eliminazione');
    }
  };

  const set = (patch: Partial<Manutenzione>) => setForm(f => ({ ...f, ...patch }));

  const totCosto = interventi.reduce((s, i) => s + (i.costo || 0), 0);

  return (
    <div className="fleet-section">
      <div className="fleet-toolbar">
        <select className="fleet-filter" value={filterMezzo} onChange={e => setFilterMezzo(parseInt(e.target.value) || '')}>
          <option value="">Tutti i veicoli</option>
          {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
        </select>
        <select className="fleet-filter" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {TIPOLOGIE.map(t => <option key={t} value={t}>{TIPO_ICONS[t]} {t.replace(/_/g, ' ')}</option>)}
        </select>
        <select className="fleet-filter" value={filterAnno} onChange={e => setFilterAnno(parseInt(e.target.value))}>
          {anni.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <button className="btn-secondary" onClick={handleExport} title="Scarica XLSX">⬇ XLSX</button>
        <button className="btn-secondary" onClick={() => importRef.current?.click()} disabled={importing} title="Carica XLSX">
          {importing ? '...' : '⬆ XLSX'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
        <button className="btn-primary" onClick={openNew}>+ Nuovo Intervento</button>
      </div>

      {error && <div className="fleet-error">{error}</div>}

      {interventi.length > 0 && (
        <div style={{ marginBottom: 12, fontSize: '0.875rem', color: '#374151' }}>
          <strong>{interventi.length}</strong> interventi — totale costi: <strong>€ {totCosto.toFixed(2)}</strong>
        </div>
      )}

      {loading ? (
        <div className="fleet-loading">Caricamento...</div>
      ) : interventi.length === 0 ? (
        <div className="fleet-stub">Nessun intervento registrato</div>
      ) : (
        <table className="fleet-table">
          <thead>
            <tr>
              <th>Veicolo</th>
              <th>Tipo</th>
              <th>Data</th>
              <th>Descrizione</th>
              <th>Officina</th>
              <th>Costo</th>
              <th>KM intervento</th>
              <th>Prossimo KM</th>
              <th>Prossima data</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {interventi.map(s => (
              <tr key={s.id}>
                <td>
                  <strong>{s.mezzo_targa}</strong>
                  {s.mezzo_modello && <><br /><span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{s.mezzo_modello}</span></>}
                </td>
                <td>{TIPO_ICONS[s.tipologia]} {s.tipologia.replace(/_/g, ' ')}</td>
                <td>{new Date(s.data).toLocaleDateString('it-IT')}</td>
                <td style={{ maxWidth: 180 }}>{s.descrizione || '—'}</td>
                <td>{s.officina || '—'}</td>
                <td>{s.costo != null ? `€ ${Number(s.costo).toFixed(2)}` : '—'}</td>
                <td>{s.km_al_intervento ? s.km_al_intervento.toLocaleString() : '—'}</td>
                <td>{s.prossimo_intervento_km ? s.prossimo_intervento_km.toLocaleString() : '—'}</td>
                <td>{s.prossimo_intervento_data ? new Date(s.prossimo_intervento_data).toLocaleDateString('it-IT') : '—'}</td>
                <td>
                  <button className="btn-icon" onClick={() => openEdit(s)}>✏️</button>
                  <button className="btn-icon btn-danger" onClick={() => handleDelete(s.id)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="fleet-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="fleet-modal" onClick={e => e.stopPropagation()}>
            <div className="fleet-modal-header">
              <h2>{editing ? 'Modifica Intervento' : 'Nuovo Intervento Manutenzione'}</h2>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>

            <div className="fleet-modal-body">
              {error && <div className="fleet-error">{error}</div>}

              <div className="form-section-title">Dati intervento</div>
              <div className="form-grid-3">
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Veicolo *</label>
                  <select value={form.mezzo_id || ''} onChange={e => set({ mezzo_id: parseInt(e.target.value) || undefined })}>
                    <option value="">— Seleziona veicolo —</option>
                    {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipologia *</label>
                  <select value={form.tipologia || ''} onChange={e => set({ tipologia: e.target.value })}>
                    {TIPOLOGIE.map(t => <option key={t} value={t}>{TIPO_ICONS[t]} {t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Data *</label>
                  <input type="date" value={form.data || ''} onChange={e => set({ data: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Costo (€)</label>
                  <input type="number" step="0.01" value={form.costo || ''} onChange={e => set({ costo: parseFloat(e.target.value) || undefined })} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Descrizione</label>
                  <input value={form.descrizione || ''} onChange={e => set({ descrizione: e.target.value })} placeholder="Es. Cambio olio e filtro" />
                </div>
                <div className="form-group">
                  <label>Officina</label>
                  <input value={form.officina || ''} onChange={e => set({ officina: e.target.value })} placeholder="Nome officina..." />
                </div>
                <div className="form-group">
                  <label>KM al momento intervento</label>
                  <input type="number" value={form.km_al_intervento || ''} onChange={e => set({ km_al_intervento: parseInt(e.target.value) || undefined })} />
                </div>
              </div>

              <div className="form-section-title">Prossimo intervento</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label>KM prossimo intervento</label>
                  <input type="number" value={form.prossimo_intervento_km || ''} onChange={e => set({ prossimo_intervento_km: parseInt(e.target.value) || undefined })} />
                </div>
                <div className="form-group">
                  <label>Data prossimo intervento</label>
                  <input type="date" value={form.prossimo_intervento_data || ''} onChange={e => set({ prossimo_intervento_data: e.target.value })} />
                </div>
              </div>

              <div className="form-section-title">Note</div>
              <div className="form-group">
                <textarea rows={2} value={form.note || ''} onChange={e => set({ note: e.target.value })} />
              </div>
            </div>

            <div className="fleet-modal-footer">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Annulla</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Salva')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
